import { useMemo } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import { OneEmptyState } from '@factorialco/f0-react/dist/experimental'
import { Money, People, Person, Crown, Split } from '@factorialco/f0-react/icons/app'
import {
  CreateProcurementTypeWorkflowDocument,
  FindWorkflowDocument,
  ProcurementTypeItemFragment,
  RequestTypeDetailDocument,
  WorkflowContainerFragment,
  WorkflowManifestFragment,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { Form } from 'modules/forms'
import { parseKey as parseTriggerFormKey } from 'modules/procurement/lib/dynamicInputCases/triggerFormCase'
import { buildElementLabelMap } from 'modules/procurement/pages/RequestTypeDetailPage/helpers/validateFormWorkflowConsistency'
import { useBannerContext } from 'modules/layout/hooks/useBannerContext'
import { VariablesConfig } from 'modules/workflows/components/Sidepanel/VariableSelectorTypes'
import { WorkflowContainer } from 'modules/workflows/components/WorkflowContainer'

import { PeopleGroupSpecsEditor } from './PeopleGroupSpecsEditor'
import { extractPeopleGroupEntityIds, formatPeopleGroupDisplayValue } from './peopleGroupConfig'
import { useProcurementHandlers } from './useProcurementHandlers'
import { useWorkflowEntityResolution } from './useWorkflowEntityResolution'
import RoleSelector from './VariableSelectors/RoleSelector'
import TeamSelector from './VariableSelectors/TeamSelector'
import TeamSelectorMulti from './VariableSelectors/TeamSelectorMulti'

type WorkflowDataInput = WorkflowManifestFragment['workflowDataInputs'][number]

function resolveLabel(name: string, elementLabels: Map<string, string>): string {
  const referenceId = parseTriggerFormKey(name)
  if (!referenceId) return name

  return elementLabels.get(referenceId) ?? name
}

function mapDynamicInputsToVariablesConfig(
  inputs: WorkflowDataInput[],
  elementLabels: Map<string, string>
): VariablesConfig {
  return inputs.reduce<VariablesConfig>((acc, input) => {
    const label = resolveLabel(input.name, elementLabels)

    if (input.enumType) {
      acc[input.name] = { type: 'singleSelect', label, options: input.enumType.options }
    } else if (input.multiSelectType) {
      acc[input.name] = {
        type: 'multiSelect',
        label,
        options: input.multiSelectType.options,
      }
    } else if (input.simpleType) {
      switch (input.simpleType.kind) {
        case 'string':
          acc[input.name] = { type: 'text', label }
          break
        case 'integer':
        case 'float':
        case 'money':
          acc[input.name] = { type: 'number', label }
          break
        case 'boolean':
          acc[input.name] = { type: 'boolean', label }
          break
      }
    }
    return acc
  }, {})
}

interface props {
  isCompleteFormEnabled: boolean
  isVendorOnboardingEnabled: boolean
  requestType: ProcurementTypeItemFragment
  workflow: WorkflowContainerFragment | null
  form?: Form
}

const WorkflowsTab = ({
  isCompleteFormEnabled,
  isVendorOnboardingEnabled,
  requestType,
  workflow,
  form,
}: props) => {
  const { company, employee } = useCurrentSession()
  const { hasBanner } = useBannerContext()
  const handlers = useProcurementHandlers(isCompleteFormEnabled, isVendorOnboardingEnabled)

  const elementLabels = useMemo(() => buildElementLabelMap(form), [form])

  // Define variables config for WorkflowContainer
  const variablesConfig: VariablesConfig = useMemo(
    () => ({
      amount: {
        type: 'number',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.purchase_request_amount'
        ),
        icon: Money,
        unit: requestType?.workflowCurrency ? requestType.workflowCurrency : undefined,
      },
      employee_id: {
        type: 'people_group_target',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.requester'
        ),
        hidden: true,
        icon: Person,
        component: PeopleGroupSpecsEditor,
        formatDisplayValue: formatPeopleGroupDisplayValue,
        extractEntityIds: extractPeopleGroupEntityIds,
      },
      role_id: {
        type: 'entity',
        entity: 'role',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.requester_role'
        ),
        icon: Crown,
        component: {
          single: RoleSelector,
        },
      },
      team_id: {
        type: 'entity',
        entity: 'team',
        label: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.variables.requester_team'
        ),
        icon: People,
        multiSelect: true,
        component: {
          single: TeamSelector,
          multi: TeamSelectorMulti,
        },
      },
      // Dynamic inputs from workflow manifest (form questions)
      ...mapDynamicInputsToVariablesConfig(
        workflow?.workflowManifest?.dynamicWorkflowDataInputs ?? [],
        elementLabels
      ),
    }),
    [
      requestType.workflowCurrency,
      workflow?.workflowManifest?.dynamicWorkflowDataInputs,
      elementLabels,
    ]
  )

  // Create workflow mutation
  const [createWorkflow, { loading: isCreating }] = useMutation(
    CreateProcurementTypeWorkflowDocument,
    {
      extractorFn: (x) => x.procurementMutations.createWorkflowProcurementType,
      refetchQueries: [RequestTypeDetailDocument, FindWorkflowDocument],
    }
  )

  const handleCreateWorkflow = async () => {
    if (!company?.id || !requestType.id) return

    try {
      const result = await createWorkflow({
        variables: {
          companyId: company.id,
          typeId: requestType.id,
          authorId: employee.id,
        },
      })

      const handled = handleResult(result, (errors) => {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({
          type: 'wrong',
          message: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.error.create_failed',
            { errors: errorMessages }
          ),
          time: 3000,
        })
      })

      if (handled.success) {
        app.setFlash({
          type: 'correct',
          message: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.success.created'
          ),
          time: 3000,
        })
      }
    } catch (error) {
      app.setFlash({
        type: 'wrong',
        message: i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.error.create_failed',
          { errors: error }
        ),
        time: 3000,
      })
    }
  }

  if (workflow) {
    const heightOffset = hasBanner ? '264px' : '220px'

    return (
      <WorkflowContainer
        disableNavigationBlocking
        heightOffset={heightOffset}
        workflow={workflow}
        handlers={handlers}
        variables={variablesConfig}
        useEntityResolution={useWorkflowEntityResolution}
        trigger={{
          title: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.title'
          ),
          description: i18n.t(
            'procurement.purchase_requests.settings.types.detail.tabs.workflows.trigger.description'
          ),
        }}
        conditionsHeaderLabel={i18n.t('workflows.sidepanel.condition.paths.conditions_header')}
        dynamicInputsHeaderLabel={i18n.t(
          'workflows.sidepanel.condition.paths.dynamic_inputs_header'
        )}
      />
    )
  }

  // Show Add Button if no workflows exist
  return (
    <Box height="full" minHeight="s600" justifyContent="center" alignItems="center" gap="s24">
      <OneEmptyState
        actions={[
          {
            label: isCreating
              ? i18n.t(
                  'procurement.purchase_requests.settings.types.detail.tabs.workflows.action.creating'
                )
              : i18n.t(
                  'procurement.purchase_requests.settings.types.detail.tabs.workflows.action.add'
                ),
            onClick: handleCreateWorkflow,
            type: 'default',
            variant: 'outline',
            icon: Split,
          },
        ]}
        description={i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.empty.description'
        )}
        emoji="🔀"
        title={i18n.t(
          'procurement.purchase_requests.settings.types.detail.tabs.workflows.empty.title'
        )}
      />
    </Box>
  )
}

export default WorkflowsTab
